import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageContainer from '../components/PageContainer';
import SEO from '../components/SEO';
import { MessageCircle, Mail, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    role: user?.role || 'sonstiges',
    name: '',
    email: user?.email || '',
    subject: '',
    topic: 'sonstiges',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Bitte fülle alle Pflichtfelder aus');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/support/contact`, formData);
      
      toast.success('Vielen Dank für deine Nachricht. Wir melden uns so schnell wie möglich bei dir.');
      
      // Reset form
      setFormData({
        role: user?.role || 'sonstiges',
        name: '',
        email: user?.email || '',
        subject: '',
        topic: 'sonstiges',
        message: ''
      });
    } catch (error) {
      console.error('Support request error:', error);
      toast.error('Fehler beim Senden der Nachricht. Bitte versuche es später erneut.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0D1]/30 via-white to-[#F4F2F2]">
      <SEO
        title="Support & Kontakt"
        description="Kontaktiere das EventConnect Support-Team. Wir helfen dir gerne bei Fragen zu deinem Account, Abo oder technischen Problemen."
        url="/support"
      />
      <Navbar user={user} onLogout={onLogout} />

      <div className="pt-24 pb-16">
        <PageContainer size="small">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Wie können wir dir helfen?
            </h1>
            <p className="text-xl text-gray-600">
              Fülle das Formular aus und wir melden uns schnellstmöglich bei dir.
            </p>
          </div>

          {/* Support Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ich bin... *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  required
                >
                  <option value="customer">Nutzer</option>
                  <option value="provider">Dienstleister</option>
                  <option value="sonstiges">Sonstiges</option>
                </select>
              </div>

              {/* Name & Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Thema *
                </label>
                <select
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  required
                >
                  <option value="technical">Technisches Problem</option>
                  <option value="subscription">Frage zum Abo</option>
                  <option value="billing">Frage zur Abrechnung</option>
                  <option value="feedback">Feedback</option>
                  <option value="sonstiges">Sonstiges</option>
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Betreff *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="Worum geht es?"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Deine Nachricht *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none resize-none"
                  placeholder="Beschreibe dein Anliegen..."
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={sending}
                className="w-full py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>Wird gesendet...</>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Nachricht senden
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Alternative Contact */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Oder kontaktiere uns direkt:</p>
            <a
              href="mailto:support@evconn.de"
              className="inline-flex items-center gap-2 text-[#2B2142] hover:text-[#1E1730] font-semibold"
            >
              <Mail className="w-5 h-5" />
              support@evconn.de
            </a>
          </div>
        </PageContainer>
      </div>

      <Footer />
    </div>
  );
};

export default SupportPage;
